import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapContainer, TileLayer, Polygon, Marker, CircleMarker, Polyline, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { normalizeToLatLng } from '../utils/geo'
import { assetIcon, taskIcon } from '../utils/icons'
import { Site, Area } from '../types'
import ActivityFeed from './ActivityFeed'
import BaseMapSwitcher from './BaseMapSwitcher'
import { formatLocalISO } from '../utils/datetime'
import { BASEMAPS } from '../utils/basemaps'
import { useBaseMap } from '../context/BaseMapContext'

// Leaflet marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

type Multi = number[][][] // [[[lat,lng]...], ...]

// Center-anchored emoji marker icon (memoized per emoji)
const emojiIconCache = new Map<string, L.DivIcon>()
function getCenteredEmojiIcon(emoji: string): L.DivIcon {
  const key = `emoji:${emoji}`
  const cached = emojiIconCache.get(key)
  if (cached) return cached
  const icon = L.divIcon({
    html: `<div class="custom-marker-base"><span class="marker-icon">${emoji}</span></div>`,
    className: 'wildlife-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15], // center
    popupAnchor: [0, -15],
  })
  emojiIconCache.set(key, icon)
  return icon
}

function toMultiFromGeo(geo: any | null | undefined): Multi {
  if (!geo) return []
  try {
    if (geo.type === 'MultiPolygon' && Array.isArray(geo.coordinates)) {
      return geo.coordinates.map((poly: any) => poly[0].map((p: any) => [p[1], p[0]]))
    }
  } catch {}
  return []
}

function toMultiFromLegacy(coords: number[][] | null | undefined): Multi {
  if (!coords || !Array.isArray(coords) || coords.length < 3) return []
  return [coords]
}

function computeBounds(multi: Multi): L.LatLngBoundsExpression | null {
  try {
    let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
    ;(multi || []).forEach(poly => {
      (poly || []).forEach(([lat, lng]) => {
        if (lat < minLat) minLat = lat
        if (lng < minLng) minLng = lng
        if (lat > maxLat) maxLat = lat
        if (lng > maxLng) maxLng = lng
      })
    })
    if (!isFinite(minLat) || !isFinite(minLng) || !isFinite(maxLat) || !isFinite(maxLng)) return null
    return [[minLat, minLng], [maxLat, maxLng]]
  } catch {
    return null
  }
}

function useFitBounds(bounds: L.LatLngBoundsExpression | null) {
  const map = useMap()
  useEffect(() => {
    if (bounds) {
      try { map.fitBounds(bounds, { padding: [40, 40] as any }) } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(bounds)])
  return map
}

interface ActivityMarker {
  id: string;
  position: [number, number];
  icon: string;
  title: string;
  kind?: 'asset' | 'task';
  asset_type?: string;
  direction_deg?: number;
  speed_mps?: number;
  count?: number;
  subcategory?: string | null;
  time_started?: string | null;
  site_name?: string | null;
  area_name?: string | null;
  status?: string | null;
}

const SiteMapView: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null)
  const [siteMulti, setSiteMulti] = useState<Multi>([])
  const [areas, setAreas] = useState<Array<{ name: string; multi: Multi }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activity, setActivity] = useState<ActivityMarker[]>([])
  const [focusPos, setFocusPos] = useState<[number, number] | null>(null)
  const [highlightPos, setHighlightPos] = useState<[number, number] | null>(null)
  const [wildlifeTypes, setWildlifeTypes] = useState<Record<string, { name: string; icon: string; color: string }>>({})
  const { baseId, setBaseId } = useBaseMap()
  const [locating, setLocating] = useState(false)
  const [wildlifeOnly, setWildlifeOnly] = useState(false)

  useEffect(() => { loadSites(); fetchWildlifeTypes() }, [])

  useEffect(() => {
    if (selectedSiteId) {
      loadSiteData(selectedSiteId)
      loadRecentActivity(selectedSiteId)
    }
  }, [selectedSiteId])

  // Refresh markers when wildlife icon map is ready/changes
  useEffect(() => {
    if (selectedSiteId) {
      loadRecentActivity(selectedSiteId)
    }
  }, [wildlifeTypes, selectedSiteId])

  const loadSites = async () => {
    try {
      setLoading(true)
      const res = await fetchWithAuth('/api/sites')
      if (!res.ok) throw new Error('Failed to load sites')
      const data = await res.json()
      setSites(data)
      if (data.length > 0) setSelectedSiteId(data[0].id)
    } catch (e: any) {
      setError(e.message || 'Failed to load sites')
    } finally {
      setLoading(false)
    }
  }

  const loadSiteData = async (siteId: number) => {
    try {
      setError(null)
      // Get site to render boundary
      const site = sites.find(s => s.id === siteId)
      const multi = site?.geometry_json ? toMultiFromGeo(site.geometry_json) : toMultiFromLegacy(site?.polygon_coords)
      setSiteMulti(multi)
      // Get areas
      const resAreas = await fetchWithAuth(`/api/sites/${siteId}/areas`)
      if (resAreas.ok) {
        const data: Area[] = await resAreas.json()
        const mapped = data.map(a => ({
          name: a.name,
          multi: a.geometry_json ? toMultiFromGeo(a.geometry_json) : toMultiFromLegacy(a.polygon_coords)
        }))
        setAreas(mapped)
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load site data')
    }
  }

  const loadRecentActivity = async (siteId: number) => {
    const markers: ActivityMarker[] = []
    try {
      // Wildlife + assets
      const assetsRes = await fetchWithAuth('/api/assets')
      if (assetsRes.ok) {
        const data = await assetsRes.json()
        const dayAgo = Date.now() - 24 * 60 * 60 * 1000
        ;(data.assets || []).forEach((a: any) => {
          if (a.site_id !== siteId) return
          if (!a.position_coords) return
          const ts = new Date(a.time_started || a.created_at || new Date()).getTime()
          if (ts >= dayAgo) {
            const pos = normalizeToLatLng(a.position_coords as [number, number]) as [number, number] | undefined
            if (!pos) return
            const icon = assetIcon(a.type, a.subcategory, wildlifeTypes)
            markers.push({
              id: `asset-${a.id}`,
              position: pos,
              icon,
              title: a.name,
              asset_type: a.type,
              direction_deg: a.direction_deg,
              speed_mps: a.speed_mps,
              count: typeof a.count === 'number' ? a.count : undefined,
              subcategory: a.subcategory || null,
              time_started: a.time_started || a.created_at || null,
              site_name: a.site_name,
              area_name: a.area_name,
              kind: 'asset'
            })
          }
        })
      }
    } catch (e) {
      // ignore
    }
    try {
      // Tasks with location
      const tasksRes = await fetchWithAuth('/api/tasks/map')
      if (tasksRes.ok) {
        const tasks = await tasksRes.json()
        const dayAgo = Date.now() - 24 * 60 * 60 * 1000
        tasks.forEach((t: any) => {
          if (t.site_id !== siteId) return
          if (!t.location_coords) return
          const ts = new Date(t.due_date || t.created_at || new Date()).getTime()
          if (ts >= dayAgo) {
            const pos = normalizeToLatLng(t.location_coords as [number, number]) as [number, number] | undefined
            if (!pos) return
            markers.push({ id: `task-${t.id}`, position: pos, icon: taskIcon(), title: t.title, site_name: t.site_name, area_name: t.area_name, status: t.status, kind: 'task' })
          }
        })
      }
    } catch (e) {
      // ignore
    }
    setActivity(markers)
  }

  const fetchWildlifeTypes = async () => {
    try {
      const res = await fetchWithAuth('/api/assets/wildlife-types')
      if (res.ok) {
        const data = await res.json()
        setWildlifeTypes(data)
      }
    } catch {}
  }

  const bounds = useMemo(() => {
    let b = computeBounds(siteMulti)
    if (!b && areas.length > 0) {
      const merged: Multi = []
      areas.forEach(a => merged.push(...a.multi))
      b = computeBounds(merged)
    }
    return b
  }, [siteMulti, areas])

  const destinationPoint = (lat: number, lng: number, bearingDeg: number, distanceM: number): [number, number] => {
    const R = 6371000
    const br = (bearingDeg * Math.PI) / 180
    const lat1 = (lat * Math.PI) / 180
    const lng1 = (lng * Math.PI) / 180
    const dr = distanceM / R
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br)
    )
    const lng2 = lng1 + Math.atan2(
      Math.sin(br) * Math.sin(dr) * Math.cos(lat1),
      Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2)
    )
    return [(lat2 * 180) / Math.PI, ((lng2 * 180) / Math.PI + 540) % 360 - 180]
  }

  const VectorArrow: React.FC<{ id: string; start: [number, number]; dir: number; speed: number }>
    = ({ id, start, dir, speed }) => {
    const map = useMap()
    const lengthM = Math.max(5, Math.min(150, speed * 12))
    const end = destinationPoint(start[0], start[1], dir, lengthM)
    // Hide if vector on screen is shorter than the icon size (~28px)
    try {
      const p1 = map.latLngToLayerPoint(start as any)
      const p2 = map.latLngToLayerPoint(end as any)
      const dx = p2.x - p1.x, dy = p2.y - p1.y
      const pix = Math.sqrt(dx*dx + dy*dy)
      if (pix < 28) return null
    } catch {}
    return (
      <>
        <Polyline key={`vec-${id}`} positions={[start, end]} pathOptions={{ color: '#ff2d55', weight: 8, opacity: 0.98, className: 'vector-line' }} />
        <Marker key={`arrow-${id}`} position={end} icon={L.divIcon({
          html: `
            <svg width=\"28\" height=\"28\" viewBox=\"0 0 28 28\" style=\"transform: rotate(${(dir - 90)}deg); transform-origin: 100% 50%; display:block;\">
              <polygon points=\"0,6 0,22 28,14\" style=\"fill:#ff2d55; stroke:#ff2d55; stroke-width:1\" />
            </svg>
          `,
          className: 'vector-arrow-icon',
          iconSize: [28, 28],
          iconAnchor: [28, 14],
        })} />
      </>
    )
  }

  const CenterOnMeButton: React.FC = () => {
    const map = useMap()
    return (
      <button
        className="btn"
        style={{ position: 'absolute', right: 12, top: 12, zIndex: 999 }}
        onClick={() => {
          setLocating(true)
          if (!('geolocation' in navigator)) { setLocating(false); return }
          navigator.geolocation.getCurrentPosition(
            (pos) => { map.setView([pos.coords.latitude, pos.coords.longitude], 15); setLocating(false) },
            () => setLocating(false),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
          )
        }}
        disabled={locating}
        title="Center map on my location"
      >
        {locating ? '📍 Locating…' : '📍 Focus on Me'}
      </button>
    )
  }

  if (loading) return <div className="loading">Loading site map…</div>
  if (error) return <div className="error">{error}</div>
  if (!selectedSiteId) return <div className="error">No sites available</div>

  const selectedSite = sites.find(s => s.id === selectedSiteId)
  const filteredActivity = wildlifeOnly ? activity.filter(m => m.kind === 'asset' && m.asset_type === 'wild_animal') : activity

  return (
    <div
      className="site-map-shell"
      style={{ paddingLeft: 'var(--site-map-gutter)', paddingRight: 'var(--site-map-gutter)', margin: 0, width: '100vw', boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      <div className="site-map-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <Link to="/app" className="btn" title="Back to app">← Back</Link>
        <h2 style={{ margin: 0 }}>🗺️ Site Map</h2>
        {sites.length > 1 && (
          <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(parseInt(e.target.value))}>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <span style={{ color: '#666' }}>{selectedSite?.name}</span>
        <div style={{ marginLeft: 'auto' }}>
          <BaseMapSwitcher value={baseId} onChange={setBaseId} />
        </div>
      </div>
      <div className="site-map-grid">
        <div className="site-map-map" style={{ position: 'relative' }}>
          <MapContainer center={[62, 15]} zoom={6} style={{ height: 'var(--site-map-height)', width: '100%', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
            <TileLayer key={baseId} url={BASEMAPS[baseId].url} attribution={BASEMAPS[baseId].attribution} {...(BASEMAPS[baseId].options || {})} />
            {bounds && <FitBounds bounds={bounds} />}
            {focusPos && <FlyToPosition position={focusPos} onDone={() => setFocusPos(null)} />}
            {highlightPos && (
              <>
                <CircleMarker center={highlightPos as any} radius={12} pathOptions={{ color: '#ff4757', weight: 2, opacity: 0.9, fillOpacity: 0.0 }} />
                <CircleMarker center={highlightPos as any} radius={5} pathOptions={{ color: '#ff4757', weight: 2, opacity: 0.9, fillColor: '#ff4757', fillOpacity: 0.7 }} />
              </>
            )}
            <CenterOnMeButton />
            {/* Site boundary */}
            {siteMulti.map((poly, idx) => (
              <Polygon key={`site-${idx}`} positions={poly as any} pathOptions={{ color: '#3498db', weight: 2, opacity: 0.7, fillColor: '#3498db', fillOpacity: 0.08 }} />
            ))}
            {/* Areas */}
            {areas.flatMap((a, aidx) => a.multi.map((poly, pidx) => (
              <Polygon key={`area-${aidx}-${pidx}`} positions={poly as any} pathOptions={{ color: '#e74c3c', weight: 2, opacity: 0.7, fillColor: '#e74c3c', fillOpacity: 0.12 }} />
            )))}
            {/* Activity markers */}
            {filteredActivity.map(m => (
              <Marker key={m.id} position={m.position} icon={getCenteredEmojiIcon(m.icon)}>
                <Popup>
                  <div className="task-popup">
                    <h4>{m.title}</h4>
                    {m.site_name && (
                      <p className="popup-location">
                        🏠 {m.site_name}
                        {m.area_name && ` → 📍 ${m.area_name}`}
                      </p>
                    )}
                    {m.kind === 'asset' && m.asset_type === 'wild_animal' && (
                      <div className="popup-meta" style={{ display: 'grid', gap: 6 }}>
                        {m.subcategory && (<div>Type: {wildlifeTypes[m.subcategory]?.name || m.subcategory}</div>)}
                        {typeof m.count === 'number' && (<div>Count: {m.count}</div>)}
                        {m.time_started && (<div>Seen: {formatLocalISO(m.time_started)}</div>)}
                        {typeof m.direction_deg === 'number' && (
                          <div>Heading: {Math.round(((m.direction_deg % 360) + 360) % 360)}°</div>
                        )}
                        {typeof m.speed_mps === 'number' && (
                          <div>Speed: {m.speed_mps.toFixed(1)} m/s</div>
                        )}
                      </div>
                    )}
                    {m.kind === 'asset' && m.asset_type !== 'wild_animal' && (
                      <div className="popup-meta">
                        {typeof m.direction_deg === 'number' && (
                          <span className="priority-badge" style={{ backgroundColor: '#3b82f6' }}>🧭 {Math.round(((m.direction_deg % 360) + 360) % 360)}°</span>
                        )}
                        {typeof m.speed_mps === 'number' && (
                          <span className="priority-badge" style={{ backgroundColor: '#10b981' }}>🏃 {m.speed_mps.toFixed(1)} m/s</span>
                        )}
                      </div>
                    )}
                    {m.kind === 'task' && m.status && (
                      <div className="popup-meta">
                        <span className="priority-badge" style={{ backgroundColor: '#64748b' }}>{m.status}</span>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            {filteredActivity.map(m => (
              (m.kind === 'asset' && typeof m.direction_deg === 'number' && typeof m.speed_mps === 'number')
                ? <VectorArrow key={`v-${m.id}`} id={m.id} start={m.position} dir={m.direction_deg!} speed={m.speed_mps!} />
                : null
            ))}
          </MapContainer>
        </div>
        <div className="site-map-sidebar" style={{ height: 'var(--site-map-height)' }}>
          <div className="site-map-sidebar-inner" style={{ height: '100%' }}>
            <ActivityFeed
              maxItems={8}
              wildlifeOnly={wildlifeOnly}
              onWildlifeOnlyChange={setWildlifeOnly}
              onLocate={(pos) => { setFocusPos(pos); setHighlightPos(pos); setTimeout(() => setHighlightPos(null), 2000) }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const FitBounds: React.FC<{ bounds: L.LatLngBoundsExpression }> = ({ bounds }) => {
  useFitBounds(bounds)
  return null
}

export default SiteMapView

const FlyToPosition: React.FC<{ position: [number, number]; onDone?: () => void }> = ({ position, onDone }) => {
  const map = useMap()
  useEffect(() => {
    if (position) {
      try {
        map.flyTo(position as any, Math.max(map.getZoom(), 15), { duration: 0.8 })
      } catch {}
      if (onDone) setTimeout(onDone, 900)
    }
  }, [position])
  return null
}
